export type AppEnvironment = "development" | "staging" | "production";
export type BrandSurface = "light" | "dark";

export interface BrandCopy {
  primaryTagline: string;
  campaignTagline: string;
  positioningLabel: string;
  logoTagline: string;
  description: string;
}

export interface ResponsiveRasterAsset {
  oneX: string;
  twoX: string;
  fourX: string;
}

export interface BrandAssets {
  product: {
    compact: Record<BrandSurface, ResponsiveRasterAsset>;
    horizontal: Record<BrandSurface, ResponsiveRasterAsset>;
    horizontalWithoutTagline: Record<BrandSurface, ResponsiveRasterAsset>;
    symbol: Record<BrandSurface, string>;
  };
  assistant: {
    horizontal: Record<BrandSurface, ResponsiveRasterAsset>;
    horizontalWithoutTagline: Record<BrandSurface, ResponsiveRasterAsset>;
    symbol: Record<BrandSurface, string>;
  };
  icons: {
    favicon: string;
    faviconIco: string;
    appleTouch: string;
    pwa192: string;
    pwa512: string;
    pwaMaskable512: string;
  };
  social: Record<BrandSurface, string>;
  emailHeader: Record<BrandSurface, string>;
}

export interface BrandTheme {
  primitive: {
    ivory: string;
    parchment: string;
    sand: string;
    sage: string;
    gold: string;
    charcoal: string;
  };
  light: {
    background: string;
    surface: string;
    surfaceMuted: string;
    textPrimary: string;
    textSecondary: string;
    action: string;
    actionText: string;
    accentText: string;
    border: string;
  };
  dark: {
    background: string;
    surface: string;
    surfaceMuted: string;
    textPrimary: string;
    textSecondary: string;
    action: string;
    actionText: string;
    accentText: string;
    border: string;
  };
}

export interface BrandConfig {
  officialProductName: string;
  shortProductName: string;
  assistantName: string;
  domain: string;
  copy: BrandCopy;
  tagline: string;
  description: string;
  legalOrganizationName: string;
  supportEmail: string;
  socialHandles: Record<"x" | "instagram" | "youtube", string>;
  oauthDisplayName: string;
  emailSenderName: string;
  pwa: { name: string; shortName: string };
  mobile: {
    androidApplicationId: string;
    iosBundleId: string;
    deepLinkScheme: string;
  };
  copyrightText: string;
  appStore: {
    title: string;
    subtitle: string;
    shortDescription: string;
    fullDescription: string;
  };
  routes: { askAssistant: string };
  assets: BrandAssets;
  theme: BrandTheme;
}

const raster = (base: string): ResponsiveRasterAsset => ({
  oneX: `${base}@1x.png`,
  twoX: `${base}@2x.png`,
  fourX: `${base}@4x.png`,
});

export const brand = {
  officialProductName: "Bible Alone",
  shortProductName: "Bible Alone",
  assistantName: "Sola",
  domain: "biblealone.app",
  copy: {
    primaryTagline: "AI that takes Scripture seriously.",
    campaignTagline: "Ask Sola. Read Scripture. Grow in Truth.",
    positioningLabel: "Scripture-centered AI",
    logoTagline: "The Bible. Alone.",
    description:
      "A general-purpose Christian AI assistant grounded in Scripture as the final authority.",
  },
  tagline: "AI that takes Scripture seriously.",
  description:
    "A general-purpose Christian AI assistant grounded in Scripture as the final authority.",
  legalOrganizationName: "Bible Alone",
  supportEmail: "support@biblealone.app",
  socialHandles: {
    x: "@BibleAloneApp",
    instagram: "@BibleAloneApp",
    youtube: "@BibleAloneApp",
  },
  oauthDisplayName: "Bible Alone",
  emailSenderName: "Bible Alone",
  pwa: {
    name: "Bible Alone",
    shortName: "Bible Alone",
  },
  mobile: {
    androidApplicationId: "app.biblealone.mobile",
    iosBundleId: "app.biblealone.mobile",
    deepLinkScheme: "biblealone",
  },
  copyrightText: "© 2026 Bible Alone. All rights reserved.",
  appStore: {
    title: "Bible Alone: Christian AI",
    subtitle: "AI that takes Scripture seriously.",
    shortDescription:
      "Ask Sola everyday questions, study Scripture, compare interpretations, and prepare faithful lessons.",
    fullDescription:
      "Bible Alone is a general-purpose Christian AI assistant for everyday life, Bible study, ministry preparation, family discipleship, and thoughtful theological comparison. Sola is an AI study tool—not Scripture, divine revelation, or a replacement for prayer, pastors, teachers, or the local church.",
  },
  routes: {
    askAssistant: "/ask-sola",
  },
  assets: {
    product: {
      compact: {
        light: raster("/brand/logos/biblealone/compact-light"),
        dark: raster("/brand/logos/biblealone/compact-dark"),
      },
      horizontal: {
        light: raster("/brand/logos/biblealone/horizontal-light"),
        dark: raster("/brand/logos/biblealone/horizontal-dark"),
      },
      horizontalWithoutTagline: {
        light: raster("/brand/logos/biblealone/horizontal-no-tagline-light"),
        dark: raster("/brand/logos/biblealone/horizontal-no-tagline-dark"),
      },
      symbol: {
        light: "/brand/logos/biblealone/symbol-light.svg",
        dark: "/brand/logos/biblealone/symbol-dark.svg",
      },
    },
    assistant: {
      horizontal: {
        light: raster("/brand/logos/sola/horizontal-light"),
        dark: raster("/brand/logos/sola/horizontal-dark"),
      },
      horizontalWithoutTagline: {
        light: raster("/brand/logos/sola/horizontal-no-tagline-light"),
        dark: raster("/brand/logos/sola/horizontal-no-tagline-dark"),
      },
      symbol: {
        light: "/brand/logos/sola/symbol-light.svg",
        dark: "/brand/logos/sola/symbol-dark.svg",
      },
    },
    icons: {
      favicon: "/brand/icons/favicon.svg",
      faviconIco: "/favicon.ico",
      appleTouch: "/brand/icons/apple-touch-icon.png",
      pwa192: "/assets/app-icon-192.png",
      pwa512: "/assets/app-icon-512.png",
      pwaMaskable512: "/assets/app-icon-maskable-512.png",
    },
    social: {
      light: "/brand/social/open-graph-light.png",
      dark: "/brand/social/open-graph-dark.png",
    },
    emailHeader: {
      light: "/brand/email/header-light.png",
      dark: "/brand/email/header-dark.png",
    },
  },
  theme: {
    primitive: {
      ivory: "#F7F4EE",
      parchment: "#E9E2D6",
      sand: "#CBB99A",
      sage: "#7A8A78",
      gold: "#B68B3D",
      charcoal: "#2B2A27",
    },
    light: {
      background: "#F7F4EE",
      surface: "#FFFFFF",
      surfaceMuted: "#E9E2D6",
      textPrimary: "#2B2A27",
      textSecondary: "#6C675E",
      action: "#526450",
      actionText: "#FFFFFF",
      accentText: "#785A1A",
      border: "#D8D0C3",
    },
    dark: {
      background: "#111210",
      surface: "#191A17",
      surfaceMuted: "#22231F",
      textPrimary: "#F7F4EE",
      textSecondary: "#BDB7AB",
      action: "#8FA18D",
      actionText: "#111210",
      accentText: "#C99B48",
      border: "#3B392F",
    },
  },
} as const satisfies BrandConfig;

export function appUrl(environment: AppEnvironment): string {
  if (environment === "development") return "http://localhost:5173";
  if (environment === "staging") return "https://test.biblealone.app";
  return "https://biblealone.app";
}
